import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ProjectsSyncResult, SyncResultProjectMetaData } from '../../../models/integration.model';
import { TranslocoService } from '@ngneat/transloco';

/**
 * Projects Sync Results Modal Component
 * Displays detailed results of project synchronization operations
 * Type-safe handling of projects-specific result data
 */
@Component({
  selector: 'app-projects-sync-results-modal',
  templateUrl: './projects-sync-results-modal.component.html',
  styleUrls: ['./projects-sync-results-modal.component.scss'],
})
export class ProjectsSyncResultsModalComponent implements OnInit {
  // Public properties
  syncResult: ProjectsSyncResult;

  constructor(
    public dialogRef: MatDialogRef<ProjectsSyncResultsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { syncResult: ProjectsSyncResult },
    private transloco: TranslocoService,
  ) {
    this.syncResult = data.syncResult;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    console.log('Projects Sync Results Modal initialized with result:', this.syncResult);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Check if sync was successful
   */
  isSuccessful(): boolean {
    return this.syncResult.status === 'success' || this.syncResult.status === 'completed';
  }

  /**
   * Get success rate percentage
   */
  getSuccessRate(): number {
    return this.syncResult.summary?.success_rate || 0;
  }

  /**
   * Check if there are project IDs to display
   */
  hasProjectIds(): boolean {
    return (
      this.getProjectIds('created').length > 0 ||
      this.getProjectIds('updated').length > 0 ||
      this.getProjectIds('matched').length > 0 ||
      this.getProjectIds('failed').length > 0
    );
  }

  /**
   * Get project IDs for a specific category
   */
  getProjectIds(
    category: 'created' | 'updated' | 'matched' | 'failed',
  ): SyncResultProjectMetaData[] {
    if (!this.syncResult.meta_data) return [];

    switch (category) {
      case 'created':
        return this.syncResult.meta_data.projects_created || [];
      case 'updated':
        return this.syncResult.meta_data.projects_updated || [];
      case 'matched':
        return this.syncResult.meta_data.projects_matched || [];
      case 'failed':
        return this.syncResult.meta_data.projects_failed || [];
      default:
        return [];
    }
  }

  /**
   * Check if there are failures to display
   */
  hasFailures(): boolean {
    return this.syncResult.failures && this.syncResult.failures.length > 0;
  }

  /**
   * Close the modal
   */
  onClose(): void {
    this.dialogRef.close(false);
  }

  /**
   * Refresh projects
   */
  onRefreshProjects(): void {
    this.dialogRef.close(true);
  }
}
